import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Ban,
  Brain,
  CalendarClock,
  FileStack,
  Mail,
  Star,
  Trophy,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../utils/api";
import { actionFailureMessage, useLongActionNotice } from "../utils/actionFeedback";
import DashboardLayout from "../components/layout/DashboardLayout";
import RecruiterSidebar from "../components/layout/RecruiterSidebar";
import CandidateEmailSendConsentModal from "../components/CandidateEmailSendConsentModal";
import {
  localizedLanguageEducationDetail,
  scoringLabel,
  scoringLanguageFromText,
} from "../utils/localizedScoringLabels";

export default function ScanHistory() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);
  const [notes, setNotes] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [activeDecisionTabs, setActiveDecisionTabs] = useState({});
  const [selectedActionIds, setSelectedActionIds] = useState([]);
  const [sendingEmailType, setSendingEmailType] = useState(null);
  const [emailModalType, setEmailModalType] = useState(null);
  const emailConsentResolverRef = useRef(null);
  const [emailSendConsent, setEmailSendConsent] = useState({
    open: false,
    provider: "",
  });
  const [historyRange, setHistoryRange] = useState("1");

  useLongActionNotice(Boolean(sendingEmailType), "Candidate email sending", 45000);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;

    const loadHistory = async () => {
      setFetching(true);

      try {
        const res = await apiFetch("/jobs/scan-history?limit=100");
        const data = await res.json();

        if (!res.ok) {
          toast.error(data?.detail || "Failed to load scan history.");
          return;
        }

        const runs = data.history || [];
        setHistory(runs);
        setNotes(
          runs.reduce((acc, run) => {
            (run.ranked_candidates || []).forEach((candidate) => {
              if (candidate.recruiter_note) acc[candidate.scan_id] = candidate.recruiter_note;
            });
            return acc;
          }, {})
        );
      } catch {
        toast.error("Failed to load scan history.");
      } finally {
        setFetching(false);
      }
    };

    loadHistory();
  }, [user]);

  const formatDateTime = (value) => {
    if (!value) return "Not available";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not available";

    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const scoreTone = (score) => {
    if (score >= 75) return "border-emerald-200 bg-emerald-50 text-emerald-700";
    if (score >= 55) return "border-amber-200 bg-amber-50 text-amber-700";
    return "border-rose-200 bg-rose-50 text-rose-700";
  };

  const filteredHistory = useMemo(() => {
    if (historyRange === "all") return history;

    const rangeDays = Number(historyRange);
    const now = Date.now();

    return history.filter((run) => {
      const date = new Date(run.scanned_at);
      if (Number.isNaN(date.getTime())) return false;

      return (now - date.getTime()) / (1000 * 60 * 60 * 24) <= rangeDays;
    });
  }, [history, historyRange]);

  const decisionLabel = (score) => {
    if (score >= 75) return "Strong Match";
    if (score >= 55) return "Good Match";
    return "Needs Review";
  };

  const getLanguageFitScore = (item) =>
    Math.max(0, Math.min(100, Number(item?.fit_score ?? 0) || 0));

  const isLanguageEvidenceItem = (item) => {
    const terms = [
      "german",
      "deutsch",
      "english",
      "englisch",
      "spanish",
      "french",
      "italian",
      "tamil",
      "c1",
      "b2",
      "b1",
      "a2",
      "a1",
      "native speaker",
      "muttersprache",
    ];
    const text = [
      item?.skill,
      item?.language,
      item?.required_level,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return terms.some((term) => text.includes(term));
  };

  const getLanguageMatch = (candidate) =>
    Array.isArray(candidate.language_match)
      ? candidate.language_match.filter(isLanguageEvidenceItem)
      : [];

  const buildLanguageEducationDetail = (candidate, scoringLanguage = "en") => {
    const qualification = candidate.top_qualification || "";
    const languageRows = getLanguageMatch(candidate);
    return localizedLanguageEducationDetail(qualification, languageRows, scoringLanguage);
  };

  const getLanguageEducationPercent = (candidate) => {
    const languageRows = getLanguageMatch(candidate);
    const languageFitPercent = languageRows.length
      ? languageRows.reduce((sum, item) => sum + getLanguageFitScore(item), 0) / languageRows.length
      : null;
    const educationPercent = candidate.top_qualification ? 60 : 0;

    return Math.max(languageFitPercent ?? 0, educationPercent);
  };

  const getScoreRows = (candidate, scoringLanguage = "en") => {
    const breakdown = candidate.scoring_breakdown || {};
    const finalScore = Math.round(candidate.match_score || 0);
    const hasCriteria =
      breakdown.must_score !== null && breakdown.must_score !== undefined
      || breakdown.preferred_score !== null && breakdown.preferred_score !== undefined;
    const toPoints = (value, max) =>
      Math.round((Math.max(0, Math.min(100, Number(value) || 0)) / 100) * max);

    if (!hasCriteria) {
      const semantic = Math.min(toPoints(breakdown.semantic_score ?? breakdown.base_score ?? finalScore, 40), finalScore);
      const languageEducationPoints = toPoints(getLanguageEducationPercent(candidate), 10);
      const support = Math.min(languageEducationPoints, Math.max(0, finalScore - semantic));
      const skill = Math.max(0, finalScore - semantic - support);

      return [
        [scoringLabel("Semantic relevance", scoringLanguage), semantic, 40, true, scoringLabel("How closely the CV text matches the job description overall, even when wording differs.", scoringLanguage)],
        [scoringLabel("Skill coverage", scoringLanguage), skill, 55, true, scoringLabel("How much of the detected job skill requirement is supported by skills found in the CV.", scoringLanguage)],
        [scoringLabel("Language / education", scoringLanguage), support, 10, support > 0, buildLanguageEducationDetail(candidate, scoringLanguage)],
      ];
    }

    const languageEducationPercent = getLanguageEducationPercent(candidate);
    const hasLanguageEducationSignal = languageEducationPercent > 0;
    const rows = [
      [scoringLabel("Semantic relevance", scoringLanguage), toPoints(breakdown.semantic_score ?? breakdown.base_score ?? finalScore, 40), 40, true, scoringLabel("How closely the CV text matches the job description before criteria are applied.", scoringLanguage)],
      [scoringLabel("Must-have skills", scoringLanguage), toPoints(breakdown.must_score ?? 0, 40), 40, breakdown.must_score !== null && breakdown.must_score !== undefined, scoringLabel("Recruiter-selected critical skills.", scoringLanguage)],
      [scoringLabel("Preferred skills", scoringLanguage), toPoints(breakdown.preferred_score ?? 0, 15), 15, breakdown.preferred_score !== null && breakdown.preferred_score !== undefined, scoringLabel("Bonus skills that improve confidence.", scoringLanguage)],
      [scoringLabel("Language / education", scoringLanguage), toPoints(languageEducationPercent, 5), 5, hasLanguageEducationSignal, buildLanguageEducationDetail(candidate, scoringLanguage)],
    ];

    return rows;
  };

  const saveNote = async (scanId, note) => {
    try {
      const res = await apiFetch(`/dashboard/my_scans/${scanId}/note`, {
        method: "POST",
        body: JSON.stringify({ note: note || "" }),
      });

      if (!res.ok) {
        const data = await res.json();
        toast.error(actionFailureMessage("Save note", data?.detail));
        return;
      }

      setNotes((prev) => ({ ...prev, [scanId]: note || "" }));
      toast.success("Note saved.");
    } catch {
      toast.error(actionFailureMessage("Save note"));
    }
  };

  const toggleShortlist = async (candidate) => {
    try {
      const res = await apiFetch(`/dashboard/my_scans/${candidate.scan_id}/shortlist`, {
        method: "POST",
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Shortlist update", data?.detail));
        return;
      }

      const updateCandidate = (item) =>
        item.scan_id === candidate.scan_id
          ? { ...item, is_shortlisted: data.is_shortlisted, is_rejected: data.is_rejected }
          : item;

      setHistory((prev) =>
        prev.map((run) => ({
          ...run,
          ranked_candidates: (run.ranked_candidates || []).map(updateCandidate),
          top_candidates: (run.top_candidates || []).map(updateCandidate),
          remaining_candidates: (run.remaining_candidates || []).map(updateCandidate),
          shortlisted_count: (run.ranked_candidates || []).map(updateCandidate).filter((item) => item.is_shortlisted).length,
        }))
      );

      setSelectedRun((prev) =>
        prev
          ? {
              ...prev,
              ranked_candidates: (prev.ranked_candidates || []).map(updateCandidate),
              top_candidates: (prev.top_candidates || []).map(updateCandidate),
              remaining_candidates: (prev.remaining_candidates || []).map(updateCandidate),
              shortlisted_count: (prev.ranked_candidates || []).map(updateCandidate).filter((item) => item.is_shortlisted).length,
            }
          : prev
      );
    } catch {
      toast.error(actionFailureMessage("Shortlist update"));
    }
  };

  const updateCandidateEverywhere = (scanId, updates) => {
    const updateCandidate = (item) =>
      item.scan_id === scanId ? { ...item, ...updates } : item;

    setHistory((prev) =>
      prev.map((run) => ({
        ...run,
        ranked_candidates: (run.ranked_candidates || []).map(updateCandidate),
        top_candidates: (run.top_candidates || []).map(updateCandidate),
        remaining_candidates: (run.remaining_candidates || []).map(updateCandidate),
        shortlisted_count: (run.ranked_candidates || []).map(updateCandidate).filter((item) => item.is_shortlisted).length,
      }))
    );

    setSelectedRun((prev) =>
      prev
        ? {
            ...prev,
            ranked_candidates: (prev.ranked_candidates || []).map(updateCandidate),
            top_candidates: (prev.top_candidates || []).map(updateCandidate),
            remaining_candidates: (prev.remaining_candidates || []).map(updateCandidate),
            shortlisted_count: (prev.ranked_candidates || []).map(updateCandidate).filter((item) => item.is_shortlisted).length,
          }
        : prev
    );
  };

  const toggleRejected = async (candidate) => {
    try {
      const res = await apiFetch(`/dashboard/my_scans/${candidate.scan_id}/reject`, {
        method: "POST",
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Rejection update", data?.detail));
        return;
      }

      updateCandidateEverywhere(candidate.scan_id, {
        is_rejected: data.is_rejected,
        is_shortlisted: data.is_shortlisted,
      });
    } catch {
      toast.error(actionFailureMessage("Rejection update"));
    }
  };

  const requestCandidateEmailSendConsent = (provider) =>
    new Promise((resolve) => {
      emailConsentResolverRef.current = resolve;
      setEmailSendConsent({
        open: true,
        provider,
      });
    });

  const closeCandidateEmailSendConsent = (accepted) => {
    const resolve = emailConsentResolverRef.current;
    emailConsentResolverRef.current = null;
    setEmailSendConsent((prev) => ({
      ...prev,
      open: false,
    }));
    resolve?.(accepted);
  };

  const ensureCandidateEmailConfigured = async (emailType) => {
    const res = await apiFetch("/candidate-email-settings");
    const settings = await res.json();

    if (!res.ok) {
      toast.error(actionFailureMessage("Check candidate email settings", settings?.detail));
      return false;
    }

    const missingConfiguration =
      emailType === "shortlisted"
        ? !settings.shortlisted_template
        : !settings.rejected_template;

    if (missingConfiguration) {
      toast.error("Add the rejection email template in Profile > App Configuration before sending candidate emails.");
      return false;
    }

    if (!settings.sender_mailbox_connected || !settings.sender_mailbox_has_send_scope) {
      toast.error("Connect Gmail or Outlook with email sending permission before sending candidate emails.");
      return false;
    }

    if (settings.send_consent_required) {
      const accepted = await requestCandidateEmailSendConsent(settings.provider);
      if (!accepted) return false;

      const consentRes = await apiFetch("/candidate-email-settings/consent", { method: "POST" });
      const consentData = await consentRes.json();

      if (!consentRes.ok) {
        toast.error(actionFailureMessage("Save email sending consent", consentData?.detail));
        return false;
      }
    }

    return true;
  };

  const sendBatchEmails = async (emailType) => {
    if (!selectedActionIds.length) {
      toast.error("Select candidates first.");
      return;
    }

    const isConfigured = await ensureCandidateEmailConfigured(emailType);
    if (!isConfigured) return;

    setSendingEmailType(emailType);

    try {
      const res = await apiFetch("/dashboard/my_scans/send-emails", {
        method: "POST",
        body: JSON.stringify({
          scan_ids: selectedActionIds,
          email_type: emailType,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Candidate email sending", data?.detail));
        return;
      }

      (data.results || []).forEach((item) => {
        if (item.status === "skipped") return;

        updateCandidateEverywhere(item.scan_id, {
          email_status: item.status,
          email_type: item.email_type || emailType,
          email_sent_at: item.email_sent_at,
          email_error: item.error,
        });
      });

      const summary = `Emails sent: ${data.sent_count}. Failed/missing: ${data.failed_count}. Skipped: ${data.skipped_count}.`;
      if (data.failed_count > 0) {
        toast.error(`${summary} Failed candidates can be retried after fixing the issue.`);
      } else {
        toast.success(summary);
      }
      setEmailModalType(null);
    } catch {
      toast.error(actionFailureMessage("Candidate email sending"));
    } finally {
      setSendingEmailType(null);
    }
  };

  const renderEmailStatus = (candidate) => {
    if (!candidate.email_status) {
      return <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">Email not sent</span>;
    }

    const typeLabel = candidate.email_type === "rejected" ? "rejection" : "shortlist";
    const statusLabel =
      candidate.email_status === "sent"
        ? `${typeLabel} email sent`
        : candidate.email_status === "missing_email"
          ? "Missing email - retry after adding email"
          : candidate.email_status === "failed"
            ? "Email failed - retry available"
            : candidate.email_status.replace("_", " ");
    const tone =
      candidate.email_status === "sent"
        ? "border border-emerald-300 bg-emerald-600 text-white shadow-sm shadow-emerald-100"
        : candidate.email_status === "missing_email"
          ? "bg-amber-100 text-amber-800"
          : "bg-rose-100 text-rose-700";

    return (
      <span
        className={`rounded-full px-2 py-1 text-xs font-semibold ${tone}`}
        title={candidate.email_error || statusLabel}
      >
        {statusLabel}
      </span>
    );
  };

  const selectedCandidates = useMemo(
    () => selectedRun?.ranked_candidates || [],
    [selectedRun]
  );

  const getEmailCandidates = (emailType) => {
    if (emailType === "shortlisted") {
      return selectedCandidates.filter(
        (candidate) =>
          candidate.is_shortlisted &&
          !(candidate.email_status === "sent" && candidate.email_type === "shortlisted")
      );
    }

    return selectedCandidates.filter(
      (candidate) =>
        candidate.is_rejected &&
        !(candidate.email_status === "sent" && candidate.email_type === "rejected")
    );
  };

  const openEmailModal = (emailType) => {
    const suggestedCandidates = getEmailCandidates(emailType);
    setSelectedActionIds(suggestedCandidates.map((candidate) => candidate.scan_id));
    setEmailModalType(emailType);
  };

  const closeEmailModal = () => {
    if (sendingEmailType) return;
    setEmailModalType(null);
  };

  const openRun = (run) => {
    setSelectedRun(run);
    setExpandedRows({});
    setSelectedActionIds([]);
  };

  const closeRun = () => {
    setSelectedRun(null);
    setExpandedRows({});
    setSelectedActionIds([]);
    setEmailModalType(null);
  };

  const toggleResultRow = (scanId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [scanId]: !prev[scanId],
    }));
  };

  const toggleActionSelection = (candidate) => {
    setSelectedActionIds((prev) =>
      prev.includes(candidate.scan_id)
        ? prev.filter((scanId) => scanId !== candidate.scan_id)
        : [...prev, candidate.scan_id]
    );
  };

  const renderScoreBreakdown = (candidate) => {
    const score = Math.round(candidate.match_score || 0);
    const scoringLanguage = scoringLanguageFromText(selectedRun?.job?.description || "");
    const rows = getScoreRows(candidate, scoringLanguage);

    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-emerald-950">{scoringLabel("Score Breakdown", scoringLanguage)}</p>
            <p className="mt-1 text-xs leading-5 text-emerald-700">
              {scoringLabel("Score rows show independent evidence signals. The final score is calculated separately from the full scoring model.", scoringLanguage)}
            </p>
          </div>
          <span className="rounded-full bg-white px-3 py-1 text-sm font-bold text-emerald-900">
            {scoringLabel("Final", scoringLanguage)} {score}%
          </span>
        </div>
        <div className="mt-3 space-y-2 rounded-md bg-white/85 p-3 text-sm">
          {rows.map(([label, points, max, available, detail]) => (
            <div key={label} className="space-y-1">
              <div className="grid grid-cols-[1fr_auto] items-center gap-4">
                <span className="font-medium text-slate-700">{label}</span>
                <span className={`font-bold tabular-nums ${available ? "text-slate-950" : "text-slate-400"}`}>
                  {available ? `${points}/${max}` : `${scoringLabel("Not set", scoringLanguage)} /${max}`}
                </span>
              </div>
              {available && detail && (
                <p className="text-xs leading-5 text-slate-500">{detail}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCandidateResult = (candidate, index) => {
    const score = Math.round(candidate.match_score || 0);
    const matchingSkills = candidate.matching_skills || [];
    const missingSkills = candidate.missing_skills || [];
    const aiStrengths = candidate.llm_strengths || [];
    const aiGaps = candidate.llm_gaps || [];
    const interviewQuestions = candidate.interview_questions || [];
    const isAiEnriched = Boolean(candidate.ai_summary || aiStrengths.length || aiGaps.length);
    const isExpanded = Boolean(expandedRows[candidate.scan_id]);
    const historyDecisionTabs = [
      { id: "technical", label: "Skills & Evidence", disabled: false },
      { id: "ai", label: "AI Review", disabled: !(candidate.ai_summary || aiStrengths.length > 0 || aiGaps.length > 0 || interviewQuestions.length > 0) },
      ...(getLanguageMatch(candidate).length > 0 ? [{ id: "language", label: "Language / Education", disabled: false }] : []),
    ];
    const selectedDecisionTab = historyDecisionTabs.some(
      (tab) => tab.id === activeDecisionTabs[candidate.scan_id] && !tab.disabled
    )
      ? activeDecisionTabs[candidate.scan_id]
      : "technical";
    const decisionTabClass = (tab) =>
      `inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg border px-3 text-xs font-bold transition ${
        selectedDecisionTab === tab.id
          ? "border-emerald-900 bg-emerald-950 text-white shadow-sm"
          : tab.disabled
          ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
          : "border-slate-200 bg-white text-slate-700 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
      }`;

    return (
      <article key={candidate.scan_id} className={`rounded-lg border bg-white p-4 shadow-sm transition ${index < 5 ? "border-emerald-300 ring-1 ring-emerald-100" : "border-slate-200"} ${isExpanded ? "ring-2 ring-emerald-100" : ""}`}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleResultRow(candidate.scan_id)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              toggleResultRow(candidate.scan_id);
            }
          }}
          className="cursor-pointer"
        >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <input
              type="checkbox"
              checked={selectedActionIds.includes(candidate.scan_id)}
              onClick={(event) => event.stopPropagation()}
              onChange={() => toggleActionSelection(candidate)}
              className="mt-2 h-4 w-4 rounded border-slate-300 text-emerald-700"
              aria-label={`Select ${candidate.filename} for email action`}
            />
            <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-bold text-slate-700">
                Rank #{index + 1}
              </span>
              {isAiEnriched && (
                <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-700">
                  AI enriched
                </span>
              )}
            </div>
            <h3 className="mt-2 truncate text-lg font-bold text-slate-950">{candidate.filename}</h3>
            <p className="mt-1 text-sm text-slate-500">{decisionLabel(score)}</p>
            <p className="mt-1 text-xs font-medium text-slate-400">
              Scanned {formatDateTime(candidate.created_at)}
            </p>
            {isExpanded && candidate.top_qualification && (
              <span className="mt-2 inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
                Education & certifications: {candidate.top_qualification}
              </span>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {candidate.is_rejected && (
                <span className="rounded-full bg-rose-100 px-2 py-1 text-xs font-semibold text-rose-700">
                  Rejected
                </span>
              )}
              {renderEmailStatus(candidate)}
            </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <span className={`rounded-full border px-3 py-1 text-sm font-bold ${scoreTone(score)}`}>
              {score}%
            </span>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                toggleShortlist(candidate);
              }}
              className={`inline-flex min-h-[34px] items-center gap-2 rounded-lg px-3 text-sm font-semibold transition ${
                candidate.is_shortlisted
                  ? "bg-amber-300 text-slate-950 hover:bg-amber-400"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              <Star size={15} />
              {candidate.is_shortlisted ? "Shortlisted" : "Shortlist"}
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                toggleRejected(candidate);
              }}
              className={`inline-flex min-h-[34px] items-center gap-2 rounded-lg px-3 text-sm font-semibold transition ${
                candidate.is_rejected
                  ? "bg-rose-700 text-white hover:bg-rose-800"
                  : "bg-rose-50 text-rose-700 hover:bg-rose-100"
              }`}
            >
              <Ban size={14} />
              {candidate.is_rejected ? "Rejected" : "Reject"}
            </button>
            <span className="text-xs font-semibold text-slate-500">
              {isExpanded ? "Hide details" : "Show details"}
            </span>
          </div>
        </div>
        </div>

        {isExpanded && (
        <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-2">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {historyDecisionTabs.map((tab) => (
                  <button
                    key={`${candidate.scan_id}-history-decision-tab-${tab.id}`}
                    type="button"
                    disabled={tab.disabled}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (!tab.disabled) {
                        const scrollY = window.scrollY;
                        setActiveDecisionTabs((prev) => ({
                          ...prev,
                          [candidate.scan_id]: tab.id,
                        }));
                        requestAnimationFrame(() => window.scrollTo({ top: scrollY, left: window.scrollX }));
                      }
                    }}
                    className={decisionTabClass(tab)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {selectedDecisionTab === "technical" && (
            <div className="rounded-xl border-2 border-emerald-400 bg-emerald-100/60 p-4 shadow-[0_0_0_3px_rgba(16,185,129,0.10)]">
              <div className="mb-4 rounded-lg bg-emerald-950 px-4 py-3 text-white shadow-sm">
                <p className="mt-1 text-base font-bold">Technical Fit</p>
                <p className="mt-1 text-xs leading-5 text-emerald-100">Skills, requirement evidence, score contribution, and technical gaps.</p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="mb-2 text-sm font-semibold text-slate-800">Detected skill evidence</p>
                  <div className="flex flex-wrap gap-2">
                    {matchingSkills.length ? matchingSkills.map((skill) => (
                      <span key={`${candidate.scan_id}-match-${skill}`} className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                        {skill}
                      </span>
                    )) : <span className="text-sm text-slate-500">No strong matches detected</span>}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="mb-2 text-sm font-semibold text-slate-800">Unconfirmed skill evidence</p>
                  <div className="flex flex-wrap gap-2">
                    {missingSkills.length ? missingSkills.map((skill) => (
                      <span key={`${candidate.scan_id}-gap-${skill}`} className="rounded-full bg-rose-50 px-3 py-1 text-xs font-medium text-rose-700">
                        {skill}
                      </span>
                    )) : <span className="text-sm text-slate-500">No major gaps detected</span>}
                  </div>
                </div>
              </div>

              <div className="mt-4">
                {renderScoreBreakdown(candidate)}
              </div>
            </div>
            )}

            {selectedDecisionTab === "language" && getLanguageMatch(candidate).length > 0 && (
              <div className="rounded-xl border-2 border-teal-400 bg-teal-100/60 p-4 shadow-[0_0_0_3px_rgba(20,184,166,0.12)]">
                <div className="rounded-lg bg-teal-800 px-4 py-3 text-white shadow-sm">
                  <p className="mt-1 text-base font-bold">Language & Qualification Fit</p>
                  <p className="mt-1 text-xs leading-5 text-teal-50">
                    Language requirements, degree evidence, certifications, and qualification signals are summarized inside the score breakdown.
                  </p>
                </div>
              </div>
            )}

            {selectedDecisionTab === "ai" && (candidate.ai_summary || aiStrengths.length > 0 || aiGaps.length > 0 || interviewQuestions.length > 0) && (
              <div className="rounded-xl border-2 border-indigo-400 bg-indigo-100/60 p-4 shadow-[0_0_0_3px_rgba(99,102,241,0.12)]">
                <div className="mb-4 rounded-lg bg-indigo-950 px-4 py-3 text-white shadow-sm">
                  <p className="mt-1 text-base font-bold">AI Evidence</p>
                  <p className="mt-1 text-xs leading-5 text-indigo-100">AI-generated reasoning for enriched candidates, including summary, evidence scores, gaps, and optional interview questions.</p>
                </div>

                {candidate.ai_summary && (
                  <div className="rounded-lg border border-emerald-100 bg-white p-3">
                    <p className="text-sm font-semibold text-emerald-950">AI Summary</p>
                    <p className="mt-2 text-sm leading-6 text-emerald-800">{candidate.ai_summary}</p>
                  </div>
                )}
                {(aiStrengths.length > 0 || aiGaps.length > 0) && (
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                      <p className="text-sm font-semibold text-slate-900">AI Matching Strengths</p>
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-slate-600">
                        {aiStrengths.map((item, itemIndex) => <li key={`${candidate.scan_id}-strength-${itemIndex}`}>{item}</li>)}
                      </ul>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                      <p className="text-sm font-semibold text-slate-900">AI Skill Gaps</p>
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-slate-600">
                        {aiGaps.map((item, itemIndex) => <li key={`${candidate.scan_id}-gap-ai-${itemIndex}`}>{item}</li>)}
                      </ul>
                    </div>
                  </div>
                )}

                {interviewQuestions.length > 0 && (
                  <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-sm font-semibold text-slate-900">Gap-Based Interview Questions</p>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-slate-600">
                      {interviewQuestions.map((item, itemIndex) => (
                        <li key={`${candidate.scan_id}-question-${itemIndex}`}>
                          {typeof item === "string" ? item : item.question || JSON.stringify(item)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-sm font-semibold text-slate-900">Recruiter Note</p>
            <textarea
              value={notes[candidate.scan_id] || ""}
              onChange={(event) => setNotes((prev) => ({ ...prev, [candidate.scan_id]: event.target.value }))}
              placeholder="Add a note for this candidate"
              rows={6}
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white p-3 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            />
            <button
              type="button"
              onClick={() => saveNote(candidate.scan_id, notes[candidate.scan_id] || "")}
              className="mt-3 inline-flex min-h-[36px] w-full items-center justify-center rounded-lg bg-emerald-800 px-4 text-sm font-semibold text-white hover:bg-emerald-900"
            >
              Save note
            </button>
          </div>
        </div>
        )}
      </article>
    );
  };

  const mainContent = (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">Previous Bulk Scan Runs</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              Open a scan card to review the same ranked candidate result view later, including AI summaries, notes, and shortlist decisions.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => navigate("/bulk-scan")}
                className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <FileStack size={16} />
                New Scan
              </button>
              <button
                type="button"
                className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg bg-emerald-800 px-4 text-sm font-semibold text-white"
              >
                <CalendarClock size={16} />
                Scan History
              </button>
            </div>
          </div>
        </div>
      </section>

      {fetching ? (
        <section className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
          Loading scan history...
        </section>
      ) : history.length === 0 ? (
        <section className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">
          <Trophy className="mx-auto text-slate-300" size={36} />
          <h3 className="mt-3 text-lg font-semibold text-slate-950">No scan history yet</h3>
          <p className="mt-2 text-sm text-slate-500">
            Run Bulk Scan from a job to build reusable scan history.
          </p>
        </section>
      ) : (
        <section className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">Scan Runs</h2>
              <p className="mt-1 text-sm leading-6 text-slate-500">
                Compact batch history grouped by job, scan date, and ranking outcome.
              </p>
            </div>
            <label className="flex flex-col gap-1 text-sm font-semibold text-slate-700">
              Date range
              <select
                value={historyRange}
                onChange={(event) => setHistoryRange(event.target.value)}
                className="min-w-[180px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              >
                <option value="1">Last 24 hours</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="all">All history</option>
              </select>
            </label>
          </div>

          {filteredHistory.length === 0 ? (
            <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
              No scan runs found for this date range.
            </div>
          ) : filteredHistory.map((run) => (
            <button
              key={run.history_id}
              type="button"
              onClick={() => setSelectedRun(run)}
              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-left shadow-sm transition hover:border-emerald-300 hover:shadow-md"
            >
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(320px,.9fr)_minmax(110px,140px)] lg:items-center">
                <div className="min-w-0">
                  <span className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                    <CalendarClock size={12} />
                    {formatDateTime(run.scanned_at)}
                  </span>
                  <h3 className="truncate text-sm font-semibold text-slate-950">{run.job?.name || "Untitled job"}</h3>
                  <p className="mt-1 truncate text-xs text-slate-500">
                    {run.job?.description || "No job description available"}
                  </p>
                </div>

                <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-4">
                  {[
                    { label: "Scanned", value: run.total },
                    { label: "Average", value: `${Math.round(run.average_score || 0)}%` },
                    { label: "Shortlisted", value: run.shortlisted_count },
                    { label: "AI enriched", value: run.ai_enriched_count },
                  ].map((item) => (
                    <div key={item.label} className="min-w-0 rounded-lg bg-slate-50 px-2.5 py-2">
                      <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                        {item.label}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-950">{item.value}</p>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-start lg:justify-end">
                  <span className={`shrink-0 rounded-full border px-3 py-1 text-sm font-bold ${scoreTone(run.top_score)}`}>
                    Top {Math.round(run.top_score || 0)}%
                  </span>
                </div>
              </div>
            </button>
          ))}
        </section>
      )}

      {selectedRun && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/60 p-4 backdrop-blur-sm">
          <div className="mx-auto my-4 max-w-6xl rounded-xl bg-slate-50 shadow-2xl">
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                    <Brain size={14} />
                    Bulk scan result
                  </span>
                  <h2 className="mt-3 text-2xl font-semibold text-slate-950">{selectedRun.job?.name}</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    {formatDateTime(selectedRun.scanned_at)} • {selectedRun.total} candidates ranked
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeRun}
                  className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <X size={16} />
                  Close
                </button>
              </div>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-500">
                  {selectedActionIds.length} candidate{selectedActionIds.length === 1 ? "" : "s"} selected for email actions.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => openEmailModal("rejected")}
                    disabled={getEmailCandidates("rejected").length === 0 || sendingEmailType !== null}
                    title={getEmailCandidates("rejected").length === 0 ? "Reject candidates before sending rejection emails." : "Review rejection email recipients"}
                    className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Mail size={16} />
                    Email Rejected
                  </button>
                </div>
              </div>
            </div>

            {emailModalType && (
              <div className="border-b border-slate-200 bg-white p-5">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                        emailModalType === "shortlisted"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-rose-50 text-rose-700"
                      }`}>
                        <Mail size={14} />
                        {emailModalType === "shortlisted" ? "Shortlist email" : "Rejection email"}
                      </span>
                      <h3 className="mt-3 text-lg font-semibold text-slate-950">
                        {emailModalType === "shortlisted" ? "Email shortlisted candidates" : "Email rejected candidates"}
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Review recipients from this scan batch before sending the configured template.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={closeEmailModal}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
                      aria-label="Close email review"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <div className="mt-4 max-h-72 overflow-y-auto space-y-3">
                    {getEmailCandidates(emailModalType).length === 0 ? (
                      <div className="rounded-lg border border-dashed border-slate-200 bg-white p-6 text-center">
                        <p className="text-sm font-semibold text-slate-800">
                          No {emailModalType === "shortlisted" ? "shortlisted" : "rejected"} candidates in this scan batch.
                        </p>
                      </div>
                    ) : (
                      getEmailCandidates(emailModalType).map((candidate) => (
                        <label
                          key={`history-email-modal-${candidate.scan_id}`}
                          className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 transition hover:border-slate-300"
                        >
                          <input
                            type="checkbox"
                            checked={selectedActionIds.includes(candidate.scan_id)}
                            onChange={() => toggleActionSelection(candidate)}
                            className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-700"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <p className="truncate text-sm font-semibold text-slate-950">{candidate.filename}</p>
                              <span className={`w-fit rounded-full border px-2 py-1 text-xs font-bold ${scoreClass(candidate.match_score || 0)}`}>
                                {Math.round(candidate.match_score || 0)}%
                              </span>
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              {candidate.candidate_email ? (
                                <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                                  {candidate.candidate_email}
                                </span>
                              ) : (
                                <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                                  Missing email
                                </span>
                              )}
                              {renderEmailStatus(candidate)}
                            </div>
                          </div>
                        </label>
                      ))
                    )}
                  </div>

                  <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-slate-600">
                      {selectedActionIds.length} candidate{selectedActionIds.length === 1 ? "" : "s"} selected for email.
                    </p>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <button
                        type="button"
                        onClick={closeEmailModal}
                        className="inline-flex min-h-[38px] items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => sendBatchEmails(emailModalType)}
                        disabled={!selectedActionIds.length || sendingEmailType !== null}
                        className={`inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                          emailModalType === "shortlisted"
                            ? "bg-emerald-800 hover:bg-emerald-900"
                            : "bg-rose-700 hover:bg-rose-800"
                        }`}
                      >
                        <Mail size={16} />
                        {sendingEmailType ? "Sending..." : emailModalType === "shortlisted" ? "Send Shortlist Email" : "Send Rejection Email"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <CandidateEmailSendConsentModal
              open={emailSendConsent.open}
              provider={emailSendConsent.provider}
              onCancel={() => closeCandidateEmailSendConsent(false)}
              onConfirm={() => closeCandidateEmailSendConsent(true)}
            />

            <div className="space-y-4 p-5">
              {selectedCandidates.slice(0, 5).length > 0 && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-center gap-2 text-emerald-950">
                    <Trophy size={18} />
                    <h3 className="font-semibold">Top 5 AI Enriched Candidates</h3>
                  </div>
                  <div className="mt-3 grid gap-2 md:grid-cols-5">
                    {selectedCandidates.slice(0, 5).map((candidate, index) => (
                      <div key={`top-${candidate.scan_id}`} className="rounded-lg bg-white p-3 shadow-sm">
                        <p className="text-xs font-bold uppercase text-emerald-600">Rank #{index + 1}</p>
                        <p className="mt-1 truncate text-sm font-semibold text-slate-950">{candidate.filename}</p>
                        <p className="mt-1 text-lg font-bold text-emerald-700">{Math.round(candidate.match_score || 0)}%</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {selectedCandidates.map(renderCandidateResult)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <DashboardLayout
      user={user}
      title="Scan History"
      subtitle="Review previous bulk scan result sets without rerunning the scan"
      eyebrow="Bulk scan archive"
      leftPanel={<RecruiterSidebar activeItem="scan_history" user={user} />}
      mainContent={mainContent}
    />
  );
}
